import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Context } from '../shared/services/context.service'; // Adapted from environment
import { MessagingWebsocketService } from './messaging-websocket.service'; // Adapted
import { TreatmentPlanNotification } from '../model/treatment-plan-notification.model'; // Adapted
import { AuthService } from '../auth/services/auth.service'; // Adapted

export interface NotificationHistoryItem {
    id: number;
    treatmentPlanId: number;
    message: string;
    event: 'CREATED' | 'STATUS_CHANGED' | 'COMMENT_UPDATED' | string;
    recipientUserId: number | null;
    recipientRole: string | null;
    createdAt: string;
    isRead?: boolean;
    read?: boolean;
    typePDT?: string;
    status?: string;
    commentaire?: string;
    // Add other fields from API response if needed
}

export interface NotificationItem {
    id?: number;
    metaId: number;
    typePDT?: string;
    message: string;
    timestamp: number; // Stored as number for easy sorting
    event?: string;
    isRead?: boolean; // Unified read status
    status?: string; // For status change logic
    commentaire?: string;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private readonly apiUrl = Context.apiUrl;
    private readonly baseUrl = `${this.apiUrl}api/notifications/treatment-history`;

    // Centralized state
    private readonly notificationCountSubject = new BehaviorSubject<number>(0);
    private readonly notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);

    // Exposed observables
    readonly notificationCount$ = this.notificationCountSubject.asObservable();
    readonly notifications$ = this.notificationsSubject.asObservable();

    private readonly newNotificationSubject = new Subject<NotificationItem>();
    readonly newNotification$ = this.newNotificationSubject.asObservable();

    constructor(
        private http: HttpClient,
        private websocketService: MessagingWebsocketService,
        private authService: AuthService
    ) {
        this.initWebSocketListener();
        this.initAuthListener();
    }

    private initAuthListener(): void {
        this.authService.authSubject.subscribe(isLoggedIn => {
            if (isLoggedIn) {
                console.log('[NotificationService] User logged in - syncing history');
                this.syncHistory();
            } else {
                this.clearAll();
            }
        });

        // Also check initial state if authSubject doesn't replay
        if (this.authService.isLogged()) {
            this.syncHistory();
        }
    }

    private syncHistory(): void {
        this.getHistory().subscribe({
            next: (history: any[]) => {
                // Determine unread items
                // API might return 'read' or 'isRead'
                console.log('[NotificationService] Synced history:', history);

                // Map API response to local items
                const mapped: NotificationItem[] = history.map(h => {
                    const isRead = (h.read !== undefined) ? h.read : (h.isRead !== undefined ? h.isRead : false);
                    return {
                        id: h.id,
                        metaId: h.treatmentPlanId || h.metaId, // Handle both
                        message: h.message || this.generateMessage(h),
                        timestamp: new Date(h.createdAt).getTime(),
                        event: h.event,
                        typePDT: h.typePDT || 'PDT',
                        isRead: isRead,
                        status: h.status,
                        commentaire: h.commentaire
                    };
                });

                // Sort by timestamp desc
                mapped.sort((a, b) => b.timestamp - a.timestamp);

                // Update state
                this.notificationsSubject.next(mapped);

                // Count unread
                const unreadCount = mapped.filter(n => !n.isRead).length;
                this.notificationCountSubject.next(unreadCount);
            },
            error: (err) => console.error('[NotificationService] Failed to sync history:', err)
        });
    }

    private initWebSocketListener(): void {
        this.websocketService.subscribeToTreatmentPlanQueue().subscribe({
            next: (evt: TreatmentPlanNotification) => {
                console.log('[NotificationService] WebSocket event:', evt);

                const fallbackType = evt.typePDT || 'PDT';
                const message = evt.message || this.generateMessage(evt);

                const newNotif: NotificationItem = {
                    id: evt.id,
                    metaId: evt.metaId,
                    typePDT: fallbackType,
                    message: message,
                    timestamp: Date.now(),
                    event: evt.event,
                    status: evt.status,
                    commentaire: evt.commentaire,
                    isRead: false
                };

                this.addNotification(newNotif);

                // Sync after delay to ensure DB ID is properly captured if missing
                if (!newNotif.id) {
                    setTimeout(() => {
                        this.syncHistory();
                    }, 2000);
                }

                this.newNotificationSubject.next(newNotif);
            },
            error: (err) => console.error('[NotificationService] WebSocket error:', err)
        });
    }

    private generateMessage(notif: any): string {
        switch (notif.event) {
            case 'CREATED':
                return `Nouveau plan de traitement créé${notif.typePDT ? ' (' + notif.typePDT + ')' : ''}`;
            case 'STATUS_CHANGED':
                const statusLabels: { [key: string]: string } = {
                    'RECU': 'Reçu',
                    'ANNULER': 'Annulé',
                    'EN_COURS': 'En cours',
                    'EXPEDIER': 'Expédié'
                };
                const statusLabel = statusLabels[notif.status] || notif.status;
                return `Statut modifié: ${statusLabel}`;
            case 'COMMENT_UPDATED':
                if (notif.commentaire) {
                    const preview = notif.commentaire.length > 60
                        ? notif.commentaire.substring(0, 60) + '...'
                        : notif.commentaire;
                    return `Commentaire: ${preview}`;
                }
                return 'Commentaire mis à jour';
            default:
                return notif.message || 'Notification de plan de traitement';
        }
    }

    getHistory(): Observable<NotificationHistoryItem[]> {
        const token = localStorage.getItem('ACCESS_TOKEN');
        const headers = { 'Authorization': token ? token : '' };
        return this.http.get<NotificationHistoryItem[]>(this.baseUrl, { headers });
    }

    addNotification(notification: NotificationItem): void {
        const current = this.notificationsSubject.value;
        // Avoid duplicates if possible (by ID or metaId+event+timestamp proximity?)
        // For now, prepend
        const updated = [notification, ...current];
        this.notificationsSubject.next(updated);

        if (!notification.isRead) {
            this.notificationCountSubject.next(this.notificationCountSubject.value + 1);
        }
    }

    markAsRead(id: number): Observable<void> {
        const token = localStorage.getItem('ACCESS_TOKEN');
        const headers = { 'Authorization': token ? token : '' };
        const url = `${this.baseUrl}/${id}/read`;

        return this.http.post<void>(url, {}, { headers }).pipe(
            // Optimistic update
        );
    }

    // Local optimistic update
    markAsReadLocal(notif: NotificationItem): void {
        if (notif.isRead) return;

        notif.isRead = true;
        const currentCount = this.notificationCountSubject.value;
        this.notificationCountSubject.next(Math.max(0, currentCount - 1));

        // Update in list
        const list = this.notificationsSubject.value;
        // Mutation is okay here as long as we don't need immutable ref check for this specific prop
        // But better to emit new array if using OnPush
        this.notificationsSubject.next([...list]);
    }

    clearAll(): void {
        this.notificationsSubject.next([]);
        this.notificationCountSubject.next(0);
    }
}
